import React, { useCallback } from "react";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useSetMultiStake } from "@/features/betslip/hooks/useStakeActions";
import { computeMultiAlert } from "@/features/betslip/utils/alerts";
import { InlineAlert } from "@/features/betslip/components/parts/InlineAlert";
import { useShareEntryModal } from "@/features/entries/hooks/useShareEntryModal";
import { ShareEntryAction } from "@/features/entries/components/share/ShareEntryAction";
import { useEntry } from "hooks/firestore/betting/useEntries";
import { useFiatCurrency } from "hooks/firestore/user/useFiatCurrency";
import type { Alert as AlertType } from "@/features/betslip/types";

export const MultiAlert = () => {
  const multiResult = useBetslipStore((s) => s.multiResult);
  const setMultiStake = useSetMultiStake();
  const dismissMultiResult = useBetslipStore((s) => s.dismissMultiResult);
  const acceptMultiOddsChanges = useBetslipStore(
    (s) => s.acceptMultiOddsChanges,
  );
  const setOverlay = useBetslipStore((s) => s.setOverlay);
  const [setShareOpen, getShareModalProps] = useShareEntryModal();
  const currency = useFiatCurrency();
  const shareReferenceId =
    multiResult?.status === "accepted" ? multiResult.betId : undefined;
  const { entry, entryLoading } = useEntry({ entryId: shareReferenceId ?? "" });

  const handleCta = useCallback(
    (cta: NonNullable<AlertType["cta"]>) => {
      switch (cta.action) {
        case "deposit":
          setOverlay({ type: "deposit" });
          break;
        case "acceptOdds":
          // The rejection is about the legs' prices: take those, clear the
          // per-leg rejections that came with it, then drop the banner.
          acceptMultiOddsChanges();
          dismissMultiResult();
          break;
        case "updateMultiStake":
          {
            const payload = cta.payload as
              | { alternativeStake?: number }
              | undefined;
            if (payload?.alternativeStake !== undefined) {
              setMultiStake(payload.alternativeStake);
            } else {
              dismissMultiResult();
            }
          }
          break;
        case "dismissMulti":
          dismissMultiResult();
          break;
        case "share":
          setShareOpen(true);
          break;
      }
    },
    [
      setMultiStake,
      dismissMultiResult,
      acceptMultiOddsChanges,
      setOverlay,
      setShareOpen,
    ],
  );

  const alert = computeMultiAlert(multiResult ?? undefined, currency, !!entry);

  return (
    <>
      <InlineAlert alert={alert} placement="card" onCta={handleCta} />
      {shareReferenceId && (
        <ShareEntryAction
          {...getShareModalProps({
            entry: entry ? [entry, entryLoading] : undefined,
            referenceId: shareReferenceId,
          })}
        />
      )}
    </>
  );
};
